/**
 * JobAnalysisPanel Component
 * Handles job category selection, job generation, and job description review for Job Analysis simulations
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import {
    FileText,
    Building,
    MapPin,
    DollarSign,
    RefreshCw,
    BookOpen,
    ListChecks,
    CheckCircle,
    Star,
    Sparkles,
    GraduationCap,
    Clock,
    Play,
    Loader2
} from 'lucide-react';
import { jobCategories } from './constants';

export const JobAnalysisPanel = ({
    selectedScenario,
    jobAnalysisStep,
    setSelectedScenario,
    selectedJobCategory,
    generatedJob,
    loadingScenario,
    generateJob,
    resetJobAnalysis,
    assignedOnly,
    startJobAnalysis
}) => {
    if (selectedScenario?.id !== 'job_analysis' || jobAnalysisStep === 'simulation') {
        return null;
    }

    // Step 1: Select job category
    if (jobAnalysisStep === 'select') {
        return (
            <Card className="col-span-full border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-purple-500/5">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-violet-500" />
                        Select Job Category
                    </CardTitle>
                    <CardDescription>
                        Choose a job category to generate a random job description for analysis
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {jobCategories.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => generateJob(cat.id)}
                                className="p-6 rounded-xl border-2 border-dashed border-muted-foreground/20 hover:border-violet-500/50 hover:bg-violet-500/5 transition-all text-left group"
                                data-testid={`job-cat-${cat.id}`}
                            >
                                <div className="text-4xl mb-3">{cat.icon}</div>
                                <h3 className="font-semibold group-hover:text-violet-500 transition-colors">{cat.name}</h3>
                                <p className="text-sm text-muted-foreground mt-1">{cat.description}</p>
                            </button>
                        ))}
                    </div>
                    {!assignedOnly && (
                        <Button variant="ghost" className="mt-4" onClick={() => setSelectedScenario(null)}>
                            ← Back to Scenarios
                        </Button>
                    )}
                </CardContent>
            </Card>
        );
    }

    // Step 2: Generating job
    if (jobAnalysisStep === 'generating') {
        return (
            <Card className="col-span-full border-violet-500/30">
                <CardContent className="py-16 flex flex-col items-center justify-center gap-4">
                    <Loader2 className="h-12 w-12 text-violet-500 animate-spin" />
                    <p className="text-lg font-medium">Generating Job Description...</p>
                    <p className="text-sm text-muted-foreground">Creating a realistic {jobCategories.find(c => c.id === selectedJobCategory)?.name} role</p>
                </CardContent>
            </Card>
        );
    }

    // Step 3: Review job description
    if (jobAnalysisStep === 'review' && generatedJob) {
        return (
            <Card className="col-span-full border-violet-500/30">
                <CardHeader className="bg-gradient-to-r from-violet-500/10 to-purple-500/10">
                    <div className="flex items-start justify-between">
                        <div>
                            <Badge className="mb-2 bg-violet-500">{generatedJob.category}</Badge>
                            <CardTitle className="text-2xl">{generatedJob.job_title}</CardTitle>
                            <CardDescription className="flex flex-wrap items-center gap-3 mt-2">
                                <span className="flex items-center gap-1">
                                    <Building className="h-4 w-4" />
                                    {generatedJob.company_type}
                                </span>
                                <span className="flex items-center gap-1">
                                    <MapPin className="h-4 w-4" />
                                    {generatedJob.location}
                                </span>
                                <span className="flex items-center gap-1">
                                    <DollarSign className="h-4 w-4" />
                                    {generatedJob.salary_range}
                                </span>
                            </CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={resetJobAnalysis}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            New Job
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <Tabs defaultValue="summary" className="w-full">
                        <TabsList className="grid w-full grid-cols-4">
                            <TabsTrigger value="summary">Summary</TabsTrigger>
                            <TabsTrigger value="responsibilities">Responsibilities</TabsTrigger>
                            <TabsTrigger value="skills">Skills</TabsTrigger>
                            <TabsTrigger value="education">Education</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="summary" className="mt-4 space-y-4">
                            <div className="p-4 rounded-lg bg-muted/50">
                                <h4 className="font-medium mb-2 flex items-center gap-2">
                                    <BookOpen className="h-4 w-4 text-violet-500" />
                                    Role Overview
                                </h4>
                                <p className="text-muted-foreground">{generatedJob.summary}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                    <h5 className="text-sm font-medium text-emerald-600 mb-1">Employment Type</h5>
                                    <p className="text-sm">{generatedJob.employment_type}</p>
                                </div>
                                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                                    <h5 className="text-sm font-medium text-blue-600 mb-1">Experience Required</h5>
                                    <p className="text-sm">{generatedJob.experience}</p>
                                </div>
                            </div>
                        </TabsContent>
                        
                        <TabsContent value="responsibilities" className="mt-4">
                            <div className="p-4 rounded-lg bg-muted/50">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                    <ListChecks className="h-4 w-4 text-violet-500" />
                                    Key Responsibilities
                                </h4>
                                <ul className="space-y-2">
                                    {generatedJob.responsibilities?.map((resp, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm">
                                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                            {resp}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </TabsContent>
                        
                        <TabsContent value="skills" className="mt-4 space-y-4">
                            <div className="p-4 rounded-lg bg-muted/50">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                    <Star className="h-4 w-4 text-amber-500" />
                                    Required Skills
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {generatedJob.required_skills?.map((skill, idx) => (
                                        <Badge key={idx} variant="secondary">{skill}</Badge>
                                    ))}
                                </div>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/50">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                    <Sparkles className="h-4 w-4 text-blue-500" />
                                    Preferred Skills
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {generatedJob.preferred_skills?.map((skill, idx) => (
                                        <Badge key={idx} variant="outline">{skill}</Badge>
                                    ))}
                                </div>
                            </div>
                        </TabsContent>
                        
                        <TabsContent value="education" className="mt-4">
                            <div className="p-4 rounded-lg bg-muted/50 space-y-4">
                                <div>
                                    <h4 className="font-medium mb-2 flex items-center gap-2">
                                        <GraduationCap className="h-4 w-4 text-violet-500" />
                                        Education Requirements
                                    </h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="p-3 rounded bg-background border">
                                            <p className="text-xs text-muted-foreground">Minimum</p>
                                            <p className="text-sm font-medium">{generatedJob.education?.minimum}</p>
                                        </div>
                                        <div className="p-3 rounded bg-background border">
                                            <p className="text-xs text-muted-foreground">Preferred</p>
                                            <p className="text-sm font-medium">{generatedJob.education?.preferred}</p>
                                        </div>
                                    </div>
                                </div>
                                {generatedJob.education?.certifications?.length > 0 && (
                                    <div>
                                        <h5 className="text-sm font-medium mb-2">Relevant Certifications</h5>
                                        <div className="flex flex-wrap gap-2">
                                            {generatedJob.education.certifications.map((cert, idx) => (
                                                <Badge key={idx} variant="outline" className="bg-violet-500/5">{cert}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                    
                    <Separator className="my-6" />
                    
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                            <Clock className="h-4 w-4 inline mr-1" />
                            Review the job description above, then start the simulation
                        </div>
                        <Button 
                            onClick={startJobAnalysis}
                            disabled={loadingScenario === 'job_analysis'}
                            className="gap-2 bg-violet-600 hover:bg-violet-700"
                            data-testid="start-job-analysis-btn"
                        >
                            {loadingScenario === 'job_analysis' ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Play className="h-4 w-4" />
                            )}
                            Start Job Analysis Simulation
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return null;
};
